import { ChangeArrow } from "@/components/event-detail/change-arrow";
import { SmartImage } from "@/components/ui/smart-image";
import { CryptoGuess } from "@/event-core/types";
import { getBrandLogoGray } from "@/lib/brand-config";
import { useCryptoConnect } from "@/store/cryptoConnect";
import { useEventConnect } from "@/store/eventConnect";
import { convertCentPriceToChancePercent, resolveUnitPrice } from "@/utils/price";
import { get, moneyFormatCurrency } from "@/utils/utils";
import BigMath from "@yn/money/math";
import { useMemo } from "react";
const BASE_CHANCE = 50;

export default function LegendSection(
    props: { guess: CryptoGuess; chartType: "crypto" | "price"; historyLatestPrice?: number },
) {
    const { guess, chartType, historyLatestPrice } = props;

    const eventConnect = useEventConnect();
    const latestPrice = useCryptoConnect( state => state.latestPrice );
    const chanceValue = useMemo( () => {
        const currentGuessData = eventConnect.guesses?.[guess.guessNo] || {};
        const yesPrice = resolveUnitPrice( get( currentGuessData, "yesPrice" ) );
        return Number( convertCentPriceToChancePercent( yesPrice ) );
    }, [ eventConnect.guesses, guess.guessNo, guess.yesRealtimePrice ] );

    const changeValue = useMemo( () => {
        return BigMath.minus( chanceValue, BASE_CHANCE );
    }, [ chanceValue ] );

    const isUp = useMemo( () => {
        return changeValue > 0;
    }, [ changeValue ] );
    const beatPrice = useMemo( () => {
        return guess.openPrice || 0;
    }, [ guess ] );

    const currentPrice = useMemo( () => {
        if ( guess.status !== 1 && guess.closePrice ) {
            return guess.closePrice;
        }
        return latestPrice ?? historyLatestPrice ?? 0;
    }, [ latestPrice, historyLatestPrice, guess ] );

    const priceDiff = useMemo( () => {
        if ( beatPrice === 0 || currentPrice === 0 ) return null;
        return BigMath.minus( currentPrice, beatPrice );
    }, [ currentPrice, beatPrice ] );

    const isPriceUp = useMemo( () => {
        if ( priceDiff === null ) return false;
        return Number( priceDiff ) > 0;
    }, [ priceDiff ] );

    if ( chartType === "price" ) {
        return (
            <div className="flex items-center">
                {/* 百分比 + Chance */}
                <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xl font-[600] text-data-primary">{guess.yesDesc} {`${chanceValue}%`}</span>
                    <span className="text-xl font-[600] text-data-primary">Chance</span>
                </div>

                {/* 涨跌幅 */}
                {changeValue !== 0 && (
                    <div className="flex items-center gap-1 flex-shrink-0 font-[600]">
                        <ChangeArrow isUp={isUp} />
                        <span className={`text-xs font-[600] ${isUp ? "text-action-accent" : "text-state-danger"}`}>
                            {BigMath.abs( changeValue, 0 )}%
                        </span>
                    </div>
                )}

                {/* Logo */}
                <div className="ml-auto flex-shrink-0">
                    <SmartImage src={getBrandLogoGray()} alt="Logo" className="h-6 w-auto" />
                </div>
            </div>
        );
    }
    return (
        <div className="flex items-center">
            <div className="text-t3">
                <div className="font-medium uppercase text-xs">price to beat</div>
                <div className="font-semibold text-base">
                    {beatPrice
                        ? moneyFormatCurrency( beatPrice, {
                            currency: "USD",
                            precision: 2,
                            useScale: false,
                            useExchange: false,
                        } )
                        : "--"}
                </div>
            </div>
            <div className="w-[1px] h-8 bg-stroke mx-5"></div>
            <div className={guess.status !== 1 ? "text-t3" : "text-[#F8931ACC]"}>
                <div className="font-medium uppercase text-xs flex items-center gap-1">
                    <span>{guess.status !== 1 ? "FINAL PRICE" : "current price"}</span>
                    {priceDiff !== null && (
                        <div className="flex items-center gap-0.5">
                            <ChangeArrow isUp={isPriceUp} />
                            <span className={isPriceUp ? "text-action-accent" : "text-state-danger"}>
                                {moneyFormatCurrency( BigMath.abs( priceDiff ), {
                                    currency: "USD",
                                    precision: 2,
                                    useScale: false,
                                    useExchange: false,
                                } )}
                            </span>
                        </div>
                    )}
                </div>
                <div className={`font-semibold text-base ${guess.status !== 1 ? "text-t1" : "text-[#F8931A]"}`}>
                    {moneyFormatCurrency( currentPrice, {
                        currency: "USD",
                        precision: 2,
                        useScale: false,
                        useExchange: false,
                    } )}
                </div>
            </div>
        </div>
    );
}
